import {
  DISCOVERY_CLIENT,
  GATEWAY_CLIENT_ID,
  GATEWAY_METADATA_URL,
  GATEWAY_PROFILE_IDS,
  PROFILE_SEEDS,
  RETIRED_DISCOVERY_CLIENT,
  RUNTIME_CLIENT,
} from "@/api/mock-data/oauth-client-profiles/seed";
import type {
  EnforcementEnvironment,
  McpServiceStatus,
} from "@/api/temp/AgentEstateService";
import {
  type AccessPurpose,
  type AuthCapabilities,
  type AuthorizationApprovalMode,
  type AuthorizationOutcome,
  type CapabilityState,
  type ConfiguredSecrets,
  type ConnectionCheck,
  type ConnectionEvidence,
  type ConnectionJob,
  type ConnectionTestKind,
  type GatewayClientSummary,
  type McpAuthFormValues,
  type McpConnectionProfileRef,
  type McpServiceAuthentication,
  type PublicClientMetadata,
  type RunConnectionTestRequest,
  type SaveMcpAuthenticationRequest,
  STALE_INPUT,
  VERSION_CONFLICT,
} from "@/api/temp/McpAuthenticationService";
import {
  activeAuthValues,
  authFingerprint,
  isAuthenticationComplete,
  isCimdRegistration,
  redactAuthValues,
} from "@/pages/MCPRegistry/authentication/authFormModel";
import { hashString } from "./generate";
import { createScenarioSwitch } from "./scenarioSwitch";

/**
 * Mock MCP Connectivity - how TrustLogix accesses each MCP Service.
 *
 * Each service has one or more connection profiles, configured with the same
 * values as manual registration's Authentication Setup. What is known about
 * reaching the service is evidence: one result per check and purpose (metadata
 * discovery or runtime), bound to the fingerprint of the inputs it ran
 * against. Saving a profile never creates access evidence, and a result whose
 * inputs have since changed is reported stale, never counted.
 *
 * Checks run as jobs that settle on the clock, so the UI sees Testing before a
 * result lands. Every result is marked `source: "MOCK"`. Nothing here carries a
 * token, a client secret or key material: secrets are recorded as configured.
 *
 * Authorization servers and the gateway client are fictional fixtures; no real
 * provider is implied to support CIMD or any particular grant.
 *
 * Scenario switches, inside mock scope:
 *  - `?mcpConnectivity=mixed|ready|notAssessed` - which checks already ran;
 *  - `?mcpExecutor=available|none` - whether metadata discovery can run
 *    without the tenant gateway.
 */

export type McpConnectivityScenario = "mixed" | "ready" | "notAssessed";

const connectivityScenario = createScenarioSwitch<McpConnectivityScenario>({
  param: "mcpConnectivity",
  storageKey: "__TLX_MCP_CONNECTIVITY_SCENARIO__",
  values: ["mixed", "ready", "notAssessed"],
  fallback: "mixed",
});

/** Specs and demos pin a scenario directly; `undefined` clears the pin. */
export const setMcpConnectivityScenario = connectivityScenario.set;
export const getMcpConnectivityScenario = connectivityScenario.get;

export type McpDiscoveryExecutor = "available" | "none";

const discoveryExecutor = createScenarioSwitch<McpDiscoveryExecutor>({
  param: "mcpExecutor",
  storageKey: "__TLX_MCP_DISCOVERY_EXECUTOR__",
  values: ["available", "none"],
  fallback: "available",
});

export const setMcpDiscoveryExecutor = discoveryExecutor.set;

/**
 * Forces one runtime authorization outcome for demo/QA, independent of the
 * seed a service would otherwise produce. `"not_verified"` (the fallback)
 * never overrides - it falls through to the seed-driven result below.
 */
export type RuntimeAuthDemoScenario =
  | "not_verified"
  | "ready"
  | "approval_required"
  | "configuration_error"
  | "access_denied"
  | "verification_failed";

const runtimeAuthScenario = createScenarioSwitch<RuntimeAuthDemoScenario>({
  param: "mcpRuntimeAuth",
  storageKey: "__TLX_MCP_RUNTIME_AUTH_SCENARIO__",
  values: [
    "not_verified",
    "ready",
    "approval_required",
    "configuration_error",
    "access_denied",
    "verification_failed",
  ],
  fallback: "not_verified",
});

export const setMcpRuntimeAuthScenario = runtimeAuthScenario.set;

/** How long each check reports Testing before its result lands. */
export const CHECK_DURATION_MS: Record<ConnectionTestKind, number> = {
  CIMD_COMPATIBILITY: 800,
  METADATA_ACCESS: 1_200,
  RUNTIME_ACCESS: 1_200,
};

/* ----------------------------------------------------- authorization servers */

export interface AuthorizationServerFixture {
  name: string;
  issuer: string;
  cimd: CapabilityState;
  grants: AuthCapabilities["grants"];
  passThrough: CapabilityState;
  /** How the tenant gateway client is registered here, if it is at all. */
  gatewayRegistration?: "CIMD" | "EXISTING_OAUTH_CLIENT";
  /** The OAuth client profile the gateway's registration here refers to. */
  gatewayProfileId?: string;
  /** How gateway approval is resolved here, when the runtime test requires it. */
  authorizationApprovalMode?: AuthorizationApprovalMode;
  /** A real, fixture-defined admin console URL - never fabricated at render time. */
  consoleUrl?: string;
}

const WORKFORCE: AuthorizationServerFixture = {
  name: "Contoso Workforce Identity",
  issuer: "https://login.contoso.com/v2.0",
  cimd: "SUPPORTED",
  grants: { clientCredentials: "SUPPORTED", tokenExchange: "SUPPORTED" },
  passThrough: "SUPPORTED",
  gatewayRegistration: "CIMD",
  gatewayProfileId: GATEWAY_PROFILE_IDS.CIMD,
};
const CODE_HOST: AuthorizationServerFixture = {
  name: "Contoso Code Hosting OAuth",
  issuer: "https://auth.code.contoso.com",
  cimd: "UNSUPPORTED",
  grants: { clientCredentials: "SUPPORTED", tokenExchange: "UNSUPPORTED" },
  passThrough: "UNKNOWN",
  gatewayRegistration: "EXISTING_OAUTH_CLIENT",
  gatewayProfileId: GATEWAY_PROFILE_IDS.CODE_HOST,
  authorizationApprovalMode: "external_admin",
  consoleUrl: "https://auth.code.contoso.com/admin/oauth-clients",
};
const WAREHOUSE: AuthorizationServerFixture = {
  name: "Contoso Warehouse OAuth",
  issuer: "https://warehouse.contoso.com/oauth",
  cimd: "SUPPORTED",
  grants: { clientCredentials: "SUPPORTED", tokenExchange: "SUPPORTED" },
  passThrough: "SUPPORTED",
  gatewayRegistration: "CIMD",
  gatewayProfileId: GATEWAY_PROFILE_IDS.CIMD,
};
const SERVICE_DESK: AuthorizationServerFixture = {
  name: "Contoso Service Desk OAuth",
  issuer: "https://servicedesk.contoso.com/oauth",
  cimd: "UNKNOWN",
  grants: { clientCredentials: "SUPPORTED", tokenExchange: "UNKNOWN" },
  passThrough: "UNKNOWN",
  authorizationApprovalMode: "provider_configuration",
};
const CRM: AuthorizationServerFixture = {
  name: "Contoso CRM Identity",
  issuer: "https://login.crm.contoso.com",
  cimd: "UNKNOWN",
  grants: { clientCredentials: "SUPPORTED", tokenExchange: "SUPPORTED" },
  passThrough: "SUPPORTED",
  gatewayRegistration: "EXISTING_OAUTH_CLIENT",
  gatewayProfileId: GATEWAY_PROFILE_IDS.CRM,
};

export const AUTHORIZATION_SERVERS: AuthorizationServerFixture[] = [
  WORKFORCE,
  CODE_HOST,
  WAREHOUSE,
  SERVICE_DESK,
  CRM,
];

export const authorizationServerFor = (
  issuer: string | undefined,
): AuthorizationServerFixture | undefined =>
  issuer ? AUTHORIZATION_SERVERS.find(server => server.issuer === issuer) : undefined;

/**
 * The tenant gateway's OAuth client for one issuer - a registration is bound
 * to the issuer it was made with, so an issuer the gateway was never
 * registered with has no applicable gateway client.
 */
export function gatewayClientFor(
  environment: EnforcementEnvironment,
  server: AuthorizationServerFixture,
): GatewayClientSummary | undefined {
  if (!environment.enabled || !server.gatewayRegistration) return undefined;
  const cimd = server.gatewayRegistration === "CIMD";
  return {
    id: `gateway-client-${hashString(server.issuer).toString(36)}`,
    displayName: environment.name ?? "Production MCP Gateway",
    environmentName: environment.name,
    issuer: server.issuer,
    clientId: cimd ? GATEWAY_METADATA_URL : GATEWAY_CLIENT_ID,
    clientProfileId: server.gatewayProfileId,
    registration: cimd
      ? { method: "CIMD", metadataUrl: GATEWAY_METADATA_URL }
      : { method: "EXISTING_OAUTH_CLIENT" },
  };
}

/** A saved OAuth client profile, as a configuration references it. */
interface ProfileRef {
  id: string;
  name: string;
  metadataUrl: string;
}

/* ------------------------------------------------------------ service seeds */

const tokenEndpointOf = (server: AuthorizationServerFixture) =>
  `${server.issuer}/token`;

const gatewayOAuth = (
  server: AuthorizationServerFixture,
  grantType: "CLIENT_CREDENTIALS" | "TOKEN_EXCHANGE",
): McpAuthFormValues => ({
  authType: "oauth",
  authConfig: {
    serviceIdentity: "GATEWAY",
    grantType,
    ...(grantType === "TOKEN_EXCHANGE" ? { exchangeType: "DELEGATION" } : {}),
    scope: "mcp.tools.read",
    tokenEndpoint: tokenEndpointOf(server),
  },
});

const customExistingClient = (
  server: AuthorizationServerFixture,
  clientId: string,
): McpAuthFormValues => ({
  authType: "oauth",
  authConfig: {
    serviceIdentity: "CUSTOM",
    grantType: "CLIENT_CREDENTIALS",
    clientRegistrationMethod: "EXISTING_OAUTH_CLIENT",
    clientId,
    scope: "mcp.tools.read",
    tokenEndpoint: tokenEndpointOf(server),
  },
});

const cimdClient = (profile: ProfileRef) => ({
  serviceIdentity: "CUSTOM",
  clientRegistrationMethod: "CIMD",
  clientMetadataSource: "MANAGED_PROFILE",
  clientMetadataProfile: { id: profile.id, name: profile.name },
  clientMetadataUrl: profile.metadataUrl,
});

const customCimdOAuth = (
  server: AuthorizationServerFixture,
): McpAuthFormValues => ({
  authType: "oauth",
  authConfig: {
    ...cimdClient(RUNTIME_CLIENT),
    grantType: "CLIENT_CREDENTIALS",
    scope: "mcp.tools.read",
    tokenEndpoint: tokenEndpointOf(server),
  },
});

const passthrough = (
  server: AuthorizationServerFixture,
  discovery: "GATEWAY" | ProfileRef,
): McpAuthFormValues => ({
  authType: "passthrough",
  authConfig: {
    ...(discovery === "GATEWAY"
      ? { serviceIdentity: "GATEWAY" }
      : cimdClient(discovery)),
    scope: "mcp.metadata.read",
    tokenEndpoint: tokenEndpointOf(server),
  },
});

interface ProfileSeed {
  id: string;
  name: string;
  /** Undefined: nothing configured yet. */
  config?: McpAuthFormValues;
  secrets?: ConfiguredSecrets;
  /** Purposes awaiting authorization at the authorization server. */
  authorizationPending?: AccessPurpose[];
  /** Metadata access was already tested when the service was first seen. */
  metadataTested?: boolean;
  runtimeTested?: boolean;
  /** Reachability failure every metadata test reports. */
  failureReason?: string;
}

interface ServiceSeed {
  server: AuthorizationServerFixture;
  profiles: ProfileSeed[];
  /** Starting authorization is cancelled at the provider. */
  authorizationCancels?: boolean;
  existingService: boolean;
  serviceStatus: McpServiceStatus;
}

const single = (
  server: AuthorizationServerFixture,
  profile: Omit<ProfileSeed, "id" | "name">,
  extra: Partial<Omit<ServiceSeed, "server" | "profiles">> = {},
): ServiceSeed => ({
  server,
  profiles: [{ id: "default", name: "Default", ...profile }],
  existingService: false,
  serviceStatus: "ACTIVE",
  ...extra,
});

/**
 * One seed per catalog server, chosen so the reachable estate covers the
 * scenarios the connectivity UI must describe honestly: tests passed, metadata
 * connected with runtime untested, authorization required, not configured,
 * failed, a JWT and a None service, CIMD with each grant, CIMD unsupported or
 * unknown at the issuer, a preserved DCR client, and a service with two
 * profiles that disagree.
 */
const SEEDS: Record<string, ServiceSeed> = {
  // Pass-through, discovery through a CIMD client.
  "mcp-m365-rm-workspace": single(
    WORKFORCE,
    { config: passthrough(WORKFORCE, DISCOVERY_CLIENT), metadataTested: true },
    { existingService: true },
  ),
  // CIMD client, Client Credentials, not tested yet.
  "mcp-kb-private-banking": single(WORKFORCE, {
    config: customCimdOAuth(WORKFORCE),
  }),
  // Gateway CIMD client, Token Exchange: runtime needs an explicit test context.
  // Runtime authorization pending, and the provider cancels it.
  "mcp-sharepoint-kyc": single(
    WORKFORCE,
    {
      config: gatewayOAuth(WORKFORCE, "TOKEN_EXCHANGE"),
      metadataTested: true,
      authorizationPending: ["RUNTIME"],
    },
    { existingService: true, authorizationCancels: true },
  ),
  // Existing client, Client Credentials: both tests passed.
  "mcp-snowflake-risk": single(WAREHOUSE, {
    config: customExistingClient(WAREHOUSE, "svc-risk-analytics"),
    secrets: { clientSecret: true },
    metadataTested: true,
    runtimeTested: true,
  }),
  // CIMD is unsupported by this issuer; the existing client stays. Two
  // profiles for one service, whose results disagree.
  "mcp-github-trading-platform": {
    server: CODE_HOST,
    profiles: [
      {
        id: "default",
        name: "Trading platform org",
        config: customExistingClient(CODE_HOST, "svc-trading-platform"),
        secrets: { clientSecret: true },
        metadataTested: true,
      },
      {
        id: "staging",
        name: "Staging org",
        config: customExistingClient(CODE_HOST, "svc-trading-staging"),
        secrets: { clientSecret: true },
        metadataTested: true,
        failureReason: "mcpEndpointUnreachable",
      },
    ],
    existingService: false,
    serviceStatus: "ACTIVE",
  },
  // A dynamically registered client, preserved as is.
  "mcp-databricks-risk-models": single(WORKFORCE, {
    config: {
      authType: "oauth",
      authConfig: {
        serviceIdentity: "CUSTOM",
        grantType: "CLIENT_CREDENTIALS",
        clientRegistrationMethod: "DCR_EXISTING",
        clientId: "dcr-7f3a9c2e",
        scope: "mcp.tools.read",
        tokenEndpoint: tokenEndpointOf(WORKFORCE),
      },
    },
    secrets: { clientSecret: true },
    metadataTested: true,
    runtimeTested: true,
  }),
  // Valid CIMD client; discovery authorization not granted yet.
  "mcp-snowflake-markets": single(WAREHOUSE, {
    config: customCimdOAuth(WAREHOUSE),
    authorizationPending: ["METADATA_DISCOVERY"],
  }),
  // Nothing configured yet.
  "mcp-databricks-trading-systems": single(WORKFORCE, {}),
  "mcp-kb-research": single(
    WORKFORCE,
    {
      config: gatewayOAuth(WORKFORCE, "CLIENT_CREDENTIALS"),
      metadataTested: true,
      failureReason: "mcpEndpointUnreachable",
    },
    { serviceStatus: "OFFLINE" },
  ),
  "mcp-sharepoint-compliance": single(WORKFORCE, {
    config: { authType: "none", authConfig: {} },
    metadataTested: true,
  }),
  "mcp-snowflake-warehouse": single(WAREHOUSE, {
    config: {
      authType: "key_pair",
      authConfig: { account: "contoso-warehouse", username: "TLX_SERVICE" },
    },
    secrets: { privateKey: true },
    metadataTested: true,
    runtimeTested: true,
  }),
  // Discovery still references a client profile that was since archived.
  "mcp-kb-hr-benefits": single(WORKFORCE, {
    config: passthrough(WORKFORCE, RETIRED_DISCOVERY_CLIENT),
    metadataTested: true,
  }),
  "mcp-sharepoint-support": single(
    WORKFORCE,
    { config: gatewayOAuth(WORKFORCE, "CLIENT_CREDENTIALS"), metadataTested: true },
    { existingService: true },
  ),
  // Nothing configured; the issuer's CIMD support is unknown.
  "mcp-servicenow-support": single(SERVICE_DESK, {}),
  "mcp-salesforce-support": single(
    CRM,
    { config: gatewayOAuth(CRM, "TOKEN_EXCHANGE"), metadataTested: true },
    { existingService: true },
  ),
};

const FALLBACK_SEEDS: ServiceSeed[] = [
  single(WORKFORCE, {
    config: passthrough(WORKFORCE, "GATEWAY"),
    metadataTested: true,
  }),
  single(WORKFORCE, {
    config: gatewayOAuth(WORKFORCE, "CLIENT_CREDENTIALS"),
    metadataTested: true,
    runtimeTested: true,
  }),
  single(WORKFORCE, { config: customCimdOAuth(WORKFORCE) }),
  single(WORKFORCE, {}),
];

/** Services with a saved configuration seed - the usage estate. */
export const seededMcpServiceIds = (): string[] => Object.keys(SEEDS);

/** The authorization server a service's configurations are bound to. */
export const authorizationServerOf = (
  serviceId: string,
): AuthorizationServerFixture => seedFor(serviceId).server;

const seedFor = (serviceId: string): ServiceSeed =>
  SEEDS[serviceId] ??
  FALLBACK_SEEDS[hashString(serviceId) % FALLBACK_SEEDS.length];

export const mcpServiceStatusFor = (serviceId: string): McpServiceStatus =>
  seedFor(serviceId).serviceStatus;

export const isExistingMcpService = (serviceId: string): boolean =>
  seedFor(serviceId).existingService;

export const mcpProfilesFor = (serviceId: string): McpConnectionProfileRef[] =>
  seedFor(serviceId).profiles.map(({ id, name }) => ({ id, name }));

/**
 * Agents outside this tenant snapshot that already use an inventory service -
 * a fixture relationship, so "shared by N agents" is never hard-coded copy.
 */
export const externalUsageFor = (serviceId: string): number =>
  seedFor(serviceId).existingService ? 8 + (hashString(serviceId) % 40) : 0;

/* ------------------------------------------------------------------- state */

interface StoredJob extends ConnectionJob {
  /** CIMD compatibility of unsaved values - redacted. */
  formValues?: McpAuthFormValues;
}

/** One connection profile as the store persists it - never a secret. */
export interface McpProfileState {
  formValues: McpAuthFormValues;
  secrets: ConfiguredSecrets;
  configured: boolean;
  version: number;
  credentialVersion: number;
  evidence: ConnectionEvidence[];
  jobs: StoredJob[];
}

/** Per service, keyed by profile ID. */
export type McpConnectivityState = Record<string, McpProfileState>;

export interface ConnectivityContext {
  environment: EnforcementEnvironment;
  now: number;
}

const iso = (time: number) => new Date(time).toISOString();

const fingerprintsOf = (
  profile: Pick<McpProfileState, "formValues" | "credentialVersion">,
): Record<AccessPurpose, string> => ({
  METADATA_DISCOVERY: authFingerprint(
    profile.formValues,
    "METADATA_DISCOVERY",
    profile.credentialVersion,
  ),
  RUNTIME: authFingerprint(
    profile.formValues,
    "RUNTIME",
    profile.credentialVersion,
  ),
});

/**
 * Whether the gateway's connection to a service is known to block enforcement:
 *  - MISSING: nothing is configured for the service;
 *  - FAILED: a current check reported a failure;
 *  - OK: neither is known. A saved profile that was never tested, whose results
 *    describe an earlier configuration, or that is still waiting for
 *    authorization at the provider is configured but not proven - that reads in
 *    the connection details, not as a setup gap in the agent aggregate.
 */
export type ConnectionBlocker = "OK" | "MISSING" | "FAILED";

export function connectionBlockerOf(profile: McpProfileState): ConnectionBlocker {
  if (!profile.configured) return "MISSING";
  const fingerprints = fingerprintsOf(profile);
  const failed = profile.evidence.some(
    entry =>
      entry.inputFingerprint === fingerprints[entry.purpose] &&
      entry.state === "FAILED",
  );
  return failed ? "FAILED" : "OK";
}

const correlationId = (seed: string) =>
  `corr-${hashString(seed).toString(16).padStart(8, "0")}`;

function evidence(
  fields: Omit<ConnectionEvidence, "id" | "source" | "evidenceType"> &
    Partial<Pick<ConnectionEvidence, "evidenceType">>,
): ConnectionEvidence {
  return {
    id: `${fields.check}:${fields.purpose}:${fields.checkedAt ?? ""}`,
    source: "MOCK",
    evidenceType: "TEST_REQUEST",
    ...fields,
  };
}

/** What a service looked like when first seen, under the active scenario. */
export function initialProfileState(
  serviceId: string,
  profileId: string,
  scenario: McpConnectivityScenario = getMcpConnectivityScenario(),
  now = Date.now(),
): McpProfileState {
  const seed = seedFor(serviceId);
  const profile =
    seed.profiles.find(entry => entry.id === profileId) ?? seed.profiles[0];
  const state: McpProfileState = {
    formValues: profile.config ?? {},
    secrets: profile.secrets ?? {},
    configured: !!profile.config,
    version: 1,
    credentialVersion: 1,
    evidence: [],
    jobs: [],
  };
  if (!state.configured) return state;

  const fingerprints = fingerprintsOf(state);
  const ready = scenario === "ready";
  const checkedAt = iso(now - 2 * 60 * 60 * 1000);

  // Pending authorization is a fact about the configuration, not a test - but
  // it only reads as required for a purpose that has actually been tested: an
  // access check the same test would have produced, exactly as a live
  // metadataAccess()/runtimeAccess() result would once it hit the same block.
  if (!ready && scenario !== "notAssessed") {
    for (const purpose of profile.authorizationPending ?? []) {
      const base = {
        purpose,
        inputFingerprint: fingerprints[purpose],
        profileVersion: "1",
        checkedAt,
      };
      state.evidence.push(
        evidence({
          ...base,
          check: "AUTHORIZATION",
          state: "BLOCKED",
          evidenceType: "CONFIGURATION_INSPECTION",
          reasonCode: "AUTHORIZATION_REQUIRED",
        }),
        evidence({
          ...base,
          check: "ACCESS",
          state: "BLOCKED",
          reasonCode: "AUTHORIZATION_REQUIRED",
        }),
      );
    }
  }
  if (scenario === "notAssessed") return state;

  if (ready || profile.metadataTested) {
    const failed = !ready && !!profile.failureReason;
    state.evidence.push(
      evidence({
        check: "ACCESS",
        purpose: "METADATA_DISCOVERY",
        state: failed ? "FAILED" : "PASSED",
        reasonCode: failed ? profile.failureReason : undefined,
        correlationId: failed
          ? correlationId(`${serviceId}:${profileId}`)
          : undefined,
        inputFingerprint: fingerprints.METADATA_DISCOVERY,
        profileVersion: "1",
        checkedAt,
        redactedPrincipalRef: "discoveryIdentity",
      }),
    );
  }
  if (ready || profile.runtimeTested) {
    state.evidence.push(
      evidence({
        check: "ACCESS",
        purpose: "RUNTIME",
        state: "PASSED",
        inputFingerprint: fingerprints.RUNTIME,
        profileVersion: "1",
        checkedAt,
        executionContextId: "synthetic-test-context",
      }),
    );
  }
  return state;
}

/* ------------------------------------------------------------ capabilities */

function capabilitiesFor(
  server: AuthorizationServerFixture,
  environment: EnforcementEnvironment,
): AuthCapabilities {
  return {
    source: "MOCK",
    evaluatedIssuer: server.issuer,
    availableAuthMethods: ["none", "oauth", "key_pair", "passthrough"],
    cimd: server.cimd,
    cimdSetupAvailable: true,
    grants: server.grants,
    customMetadataReferenceAllowed: true,
    canRunMetadataTest: true,
    // A runtime test needs the tenant's enforcement path to send it through.
    canRunRuntimeTest: environment.enabled,
    canRunDiscoveryWithoutGateway: discoveryExecutor.get() === "available",
    authorizationApprovalMode: server.authorizationApprovalMode ?? "interactive",
    passThroughCompatibility: server.passThrough,
    blockedReasons: environment.enabled ? [] : ["ENFORCEMENT_REQUIRED"],
  };
}

/* ------------------------------------------------------------- job results */

const authConfigOf = (values: McpAuthFormValues) =>
  (values.authConfig as Record<string, unknown> | undefined) ?? {};

const purposeOfCimd = (values: McpAuthFormValues): AccessPurpose =>
  values.authType === "passthrough" ? "METADATA_DISCOVERY" : "RUNTIME";

const hasCurrentAuthorizationBlock = (
  profile: McpProfileState,
  purpose: AccessPurpose,
  fingerprint: string,
) => {
  const latest = [...profile.evidence]
    .reverse()
    .find(e => e.check === "AUTHORIZATION" && e.purpose === purpose);
  return (
    latest?.state === "BLOCKED" && latest.inputFingerprint === fingerprint
  );
};

type CheckResult = Pick<
  ConnectionEvidence,
  "check" | "state" | "reasonCode" | "correlationId"
>;

function cimdCompatibility(
  values: McpAuthFormValues,
  server: AuthorizationServerFixture,
  gatewayClient: GatewayClientSummary | undefined,
): CheckResult[] {
  const authConfig = authConfigOf(values);
  const viaGateway = authConfig.serviceIdentity === "GATEWAY";
  const metadataUrl = String(
    viaGateway
      ? (gatewayClient?.registration.metadataUrl ?? "")
      : (authConfig.clientMetadataUrl ?? ""),
  );
  const grant =
    values.authType === "oauth" && authConfig.grantType === "TOKEN_EXCHANGE"
      ? server.grants.tokenExchange
      : server.grants.clientCredentials;

  const support: CheckResult =
    server.cimd === "SUPPORTED"
      ? { check: "SERVER_SUPPORT", state: "PASSED" }
      : server.cimd === "UNSUPPORTED"
        ? {
            check: "SERVER_SUPPORT",
            state: "FAILED",
            reasonCode: "CIMD_UNSUPPORTED",
          }
        : {
            check: "SERVER_SUPPORT",
            state: "BLOCKED",
            reasonCode: "CIMD_SUPPORT_UNKNOWN",
          };

  let document: CheckResult = { check: "METADATA_DOCUMENT", state: "PASSED" };
  if (!metadataUrl) {
    document = {
      check: "METADATA_DOCUMENT",
      state: "BLOCKED",
      reasonCode: "METADATA_NOT_CONFIGURED",
    };
  } else if (metadataUrl.includes("mismatch")) {
    document = {
      check: "METADATA_DOCUMENT",
      state: "FAILED",
      reasonCode: "CLIENT_ID_MISMATCH",
    };
  } else if (metadataUrl.includes("blocked")) {
    document = {
      check: "METADATA_DOCUMENT",
      state: "FAILED",
      reasonCode: "METADATA_FETCH_REJECTED",
      correlationId: correlationId(metadataUrl),
    };
  }

  const grantResult: CheckResult =
    grant === "SUPPORTED"
      ? { check: "GRANT_COMPATIBILITY", state: "PASSED" }
      : grant === "UNSUPPORTED"
        ? {
            check: "GRANT_COMPATIBILITY",
            state: "FAILED",
            reasonCode: "GRANT_UNSUPPORTED",
          }
        : {
            check: "GRANT_COMPATIBILITY",
            state: "BLOCKED",
            reasonCode: "GRANT_SUPPORT_UNKNOWN",
          };

  // A managed profile carries its signing key reference; a bare URL does not.
  const clientAuth: CheckResult =
    viaGateway || authConfig.clientMetadataSource !== "MANAGED_EXTERNAL_URL"
      ? { check: "CLIENT_AUTHENTICATION", state: "PASSED" }
      : {
          check: "CLIENT_AUTHENTICATION",
          state: "BLOCKED",
          reasonCode: "CLIENT_AUTH_PROFILE_MISSING",
        };

  return [support, document, grantResult, clientAuth];
}

function metadataAccess(
  serviceId: string,
  profile: McpProfileState,
  profileSeed: ProfileSeed,
  server: AuthorizationServerFixture,
  gatewayClient: GatewayClientSummary | undefined,
  fingerprint: string,
): CheckResult {
  const values = activeAuthValues(profile.formValues);
  const authConfig = authConfigOf(values);
  const check: ConnectionCheck = "ACCESS";
  const usesOAuthClient =
    values.authType === "oauth" || values.authType === "passthrough";

  if (usesOAuthClient && authConfig.serviceIdentity === "GATEWAY") {
    if (!gatewayClient) {
      return { check, state: "BLOCKED", reasonCode: "GATEWAY_UNAVAILABLE" };
    }
  } else if (usesOAuthClient && discoveryExecutor.get() === "none") {
    return {
      check,
      state: "BLOCKED",
      reasonCode: "EXECUTION_CONTEXT_REQUIRED",
    };
  }
  if (
    hasCurrentAuthorizationBlock(profile, "METADATA_DISCOVERY", fingerprint)
  ) {
    return { check, state: "BLOCKED", reasonCode: "AUTHORIZATION_REQUIRED" };
  }
  if (usesOAuthClient && isCimdRegistration(authConfig)) {
    if (server.cimd === "UNSUPPORTED") {
      return { check, state: "FAILED", reasonCode: "CIMD_UNSUPPORTED" };
    }
    const url = String(authConfig.clientMetadataUrl ?? "");
    if (url.includes("mismatch") || url.includes("blocked")) {
      return {
        check,
        state: "FAILED",
        reasonCode: "CLIENT_REGISTRATION_FAILED",
        correlationId: correlationId(url),
      };
    }
  }
  if (profileSeed.failureReason) {
    return {
      check,
      state: "FAILED",
      reasonCode: profileSeed.failureReason,
      correlationId: correlationId(`${serviceId}:${fingerprint}`),
    };
  }
  return { check, state: "PASSED" };
}

function runtimeAccess(
  profile: McpProfileState,
  environment: EnforcementEnvironment,
  fingerprint: string,
): CheckResult {
  const values = activeAuthValues(profile.formValues);
  const authConfig = authConfigOf(values);
  const check: ConnectionCheck = "ACCESS";

  // Demo/QA override: forces one outcome regardless of the seed. Only when
  // explicitly pinned - the fallback ("not_verified") always falls through.
  switch (runtimeAuthScenario.get()) {
    case "ready":
      return { check, state: "PASSED" };
    case "approval_required":
      return { check, state: "BLOCKED", reasonCode: "AUTHORIZATION_REQUIRED" };
    case "configuration_error":
      return { check, state: "BLOCKED", reasonCode: "CONFIGURATION_INCOMPLETE" };
    case "access_denied":
      return { check, state: "FAILED", reasonCode: "RUNTIME_ACCESS_DENIED" };
    case "verification_failed":
      return {
        check,
        state: "FAILED",
        reasonCode: "RUNTIME_VERIFICATION_FAILED",
        correlationId: correlationId(`${fingerprint}:verify`),
      };
    case "not_verified":
    default:
      break;
  }

  if (!environment.enabled) {
    return { check, state: "BLOCKED", reasonCode: "ENFORCEMENT_REQUIRED" };
  }
  if (hasCurrentAuthorizationBlock(profile, "RUNTIME", fingerprint)) {
    return { check, state: "BLOCKED", reasonCode: "AUTHORIZATION_REQUIRED" };
  }
  // Caller-token modes are only testable with an explicit caller context.
  if (
    values.authType === "passthrough" ||
    authConfig.grantType === "TOKEN_EXCHANGE"
  ) {
    return { check, state: "BLOCKED", reasonCode: "TEST_CONTEXT_REQUIRED" };
  }
  return { check, state: "PASSED" };
}

/* ----------------------------------------------------------- derivations */

interface ServiceContext extends ConnectivityContext {
  serviceId: string;
  profileId: string;
}

function contextFor(ctx: ServiceContext) {
  const seed = seedFor(ctx.serviceId);
  const profileSeed =
    seed.profiles.find(p => p.id === ctx.profileId) ?? seed.profiles[0];
  return {
    seed,
    profileSeed,
    gatewayClient: gatewayClientFor(ctx.environment, seed.server),
  };
}

/** Replaces the evidence one job produced, keeping everything else. */
const record = (
  profile: McpProfileState,
  results: ConnectionEvidence[],
): ConnectionEvidence[] => [
  ...profile.evidence.filter(
    e =>
      !results.some(r => r.check === e.check && r.purpose === e.purpose),
  ),
  ...results,
];

/** Moves every job whose time is up into evidence. Pure: returns a new state. */
export function settleJobs(
  profile: McpProfileState,
  ctx: ServiceContext,
): McpProfileState {
  const done = profile.jobs.filter(
    job => ctx.now - Date.parse(job.startedAt) >= CHECK_DURATION_MS[job.kind],
  );
  if (!done.length) return profile;
  const { profileSeed, seed, gatewayClient } = contextFor(ctx);
  let next: McpProfileState = {
    ...profile,
    jobs: profile.jobs.filter(job => !done.includes(job)),
  };
  for (const job of done) {
    const checkedAt = iso(Date.parse(job.startedAt) + CHECK_DURATION_MS[job.kind]);
    const base = {
      purpose: job.purpose,
      inputFingerprint: job.inputFingerprint,
      profileVersion: String(profile.version),
      checkedAt,
    };
    let results: CheckResult[];
    if (job.kind === "CIMD_COMPATIBILITY") {
      results = cimdCompatibility(
        job.formValues ?? next.formValues,
        seed.server,
        gatewayClient,
      );
    } else if (job.kind === "METADATA_ACCESS") {
      results = [
        metadataAccess(
          ctx.serviceId,
          next,
          profileSeed,
          seed.server,
          gatewayClient,
          job.inputFingerprint,
        ),
      ];
    } else {
      results = [
        runtimeAccess(next, ctx.environment, job.inputFingerprint),
      ];
    }
    next = {
      ...next,
      evidence: record(
        next,
        results.map(result =>
          evidence({
            ...base,
            ...result,
            evidenceType:
              job.kind === "CIMD_COMPATIBILITY"
                ? "CONFIGURATION_INSPECTION"
                : "TEST_REQUEST",
            redactedPrincipalRef:
              job.kind === "METADATA_ACCESS" ? "discoveryIdentity" : undefined,
            executionContextId:
              job.kind === "RUNTIME_ACCESS"
                ? "synthetic-test-context"
                : undefined,
          }),
        ),
      ),
    };
  }
  return next;
}

/** A configuration fact, derived on read - never stored, never a test. */
function runtimeConfiguration(
  profile: McpProfileState,
  server: AuthorizationServerFixture,
  fingerprint: string,
): ConnectionEvidence {
  const values = activeAuthValues(profile.formValues);
  const base = {
    check: "CONFIGURATION" as const,
    purpose: "RUNTIME" as const,
    inputFingerprint: fingerprint,
    profileVersion: String(profile.version),
    evidenceType: "CONFIGURATION_INSPECTION" as const,
  };
  if (!profile.configured) return evidence({ ...base, state: "NOT_ASSESSED" });
  if (values.authType === "passthrough" && server.passThrough !== "SUPPORTED") {
    return evidence({
      ...base,
      state: "BLOCKED",
      reasonCode: "PASSTHROUGH_REVIEW_REQUIRED",
    });
  }
  return evidence({
    ...base,
    state: isAuthenticationComplete(values, { isEdit: true })
      ? "PASSED"
      : "BLOCKED",
    reasonCode: isAuthenticationComplete(values, { isEdit: true })
      ? undefined
      : "CONFIGURATION_INCOMPLETE",
  });
}

export function authenticationFor(
  profile: McpProfileState,
  ctx: ServiceContext & { sharedByAgentCount: number },
): McpServiceAuthentication {
  const { seed, gatewayClient } = contextFor(ctx);
  const fingerprints = fingerprintsOf(profile);
  // A result whose inputs changed since it ran describes another configuration.
  const current = profile.evidence.map(entry =>
    entry.inputFingerprint === fingerprints[entry.purpose]
      ? entry
      : { ...entry, state: "STALE" as const, recordedState: entry.state },
  );
  return {
    profile: {
      serviceId: ctx.serviceId,
      profileId: ctx.profileId,
      version: String(profile.version),
      credentialVersion: profile.credentialVersion,
      configured: profile.configured,
      draft: !seed.existingService,
      formValues: profile.formValues,
      secrets: profile.secrets,
      profiles: mcpProfilesFor(ctx.serviceId),
      sharedByAgentCount: ctx.sharedByAgentCount,
    },
    capabilities: capabilitiesFor(seed.server, ctx.environment),
    gatewayClient,
    authorizationServer: {
      name: seed.server.name,
      issuer: seed.server.issuer,
      consoleUrl: seed.server.consoleUrl,
    },
    fingerprints,
    evidence: [
      ...current,
      runtimeConfiguration(profile, seed.server, fingerprints.RUNTIME),
    ],
    jobs: profile.jobs.map(({ formValues: _draft, ...job }) => job),
  };
}

/* --------------------------------------------------------------- mutations */

const conflict = () =>
  Object.assign(
    new Error("This connection profile changed since it was loaded."),
    { code: VERSION_CONFLICT },
  );

export function saveProfile(
  profile: McpProfileState,
  request: SaveMcpAuthenticationRequest,
): McpProfileState {
  if (request.version !== String(profile.version)) throw conflict();
  const values = activeAuthValues(request.formValues);
  if (!isAuthenticationComplete(values, { isEdit: profile.configured })) {
    throw new Error("The authentication configuration is incomplete.");
  }
  // Belt and braces: whatever the caller sent, secrets are never stored.
  const { formValues } = redactAuthValues(values);
  const secrets = { ...profile.secrets };
  request.secretsReplaced.forEach(key => {
    secrets[key] = true;
  });
  return {
    ...profile,
    formValues,
    secrets,
    configured: true,
    version: profile.version + 1,
    credentialVersion:
      profile.credentialVersion + (request.secretsReplaced.length ? 1 : 0),
  };
}

export function startJob(
  profile: McpProfileState,
  request: RunConnectionTestRequest,
  now: number,
): { profile: McpProfileState; job: ConnectionJob } {
  const draft =
    request.kind === "CIMD_COMPATIBILITY" && request.formValues
      ? redactAuthValues(activeAuthValues(request.formValues)).formValues
      : undefined;
  const purpose: AccessPurpose =
    request.kind === "METADATA_ACCESS"
      ? "METADATA_DISCOVERY"
      : request.kind === "RUNTIME_ACCESS"
        ? "RUNTIME"
        : purposeOfCimd(draft ?? profile.formValues);
  const expected = draft
    ? authFingerprint(draft, purpose, profile.credentialVersion)
    : fingerprintsOf(profile)[purpose];
  if (request.inputFingerprint !== expected) {
    throw Object.assign(
      new Error("The configuration changed; reload before testing."),
      { code: STALE_INPUT },
    );
  }
  if (!draft && !profile.configured) {
    throw new Error("Configure authentication before testing it.");
  }
  // One job per check and purpose at a time; a repeat request is a no-op.
  const running = profile.jobs.find(
    job => job.kind === request.kind && job.purpose === purpose,
  );
  if (running) return { profile, job: running };
  const job: StoredJob = {
    id: `${request.kind}:${purpose}:${now}`,
    kind: request.kind,
    purpose,
    inputFingerprint: expected,
    startedAt: iso(now),
    formValues: draft,
  };
  return { profile: { ...profile, jobs: [...profile.jobs, job] }, job };
}

export function authorize(
  profile: McpProfileState,
  serviceId: string,
  purpose: AccessPurpose,
  now: number,
): { profile: McpProfileState; outcome: AuthorizationOutcome } {
  if (seedFor(serviceId).authorizationCancels) {
    return { profile, outcome: "CANCELLED" };
  }
  const fingerprint = fingerprintsOf(profile)[purpose];
  // The access result recorded while approval was pending is no longer
  // current once it is granted - cleared, so it reads as not yet tested
  // again rather than staying "blocked" until the next explicit test.
  const cleared: McpProfileState = {
    ...profile,
    evidence: profile.evidence.filter(
      entry =>
        !(
          entry.check === "ACCESS" &&
          entry.purpose === purpose &&
          entry.reasonCode === "AUTHORIZATION_REQUIRED"
        ),
    ),
  };
  return {
    outcome: "AUTHORIZED",
    profile: {
      ...cleared,
      evidence: record(cleared, [
        evidence({
          check: "AUTHORIZATION",
          purpose,
          state: "PASSED",
          evidenceType: "CONFIGURATION_INSPECTION",
          inputFingerprint: fingerprint,
          profileVersion: String(profile.version),
          checkedAt: iso(now),
        }),
      ]),
    },
  };
}

/** Bumps a profile's version, as another administrator's save would. */
export const concurrentEdit = (profile: McpProfileState): McpProfileState => ({
  ...profile,
  version: profile.version + 1,
});

/**
 * The public metadata document behind a client metadata URL, as a backend
 * would return it after fetching and sanitizing it - plain JSON fields.
 */
export function clientMetadataFor(
  metadataUrl: string,
  now = Date.now(),
): PublicClientMetadata {
  if (metadataUrl.includes("blocked")) {
    throw Object.assign(
      new Error("The metadata URL was rejected by the outbound access policy."),
      { code: "METADATA_FETCH_REJECTED", correlationId: correlationId(metadataUrl) },
    );
  }
  const managed = PROFILE_SEEDS.find(
    profile =>
      profile.registration.kind === "CIMD" &&
      profile.registration.clientIdUrl === metadataUrl,
  );
  return {
    metadataUrl,
    fetchedAt: iso(now),
    source: "MOCK",
    document: {
      // A mismatching document is shown as fetched - never repaired.
      client_id: metadataUrl.includes("mismatch")
        ? metadataUrl.replace("mismatch", "other-client")
        : metadataUrl,
      client_name:
        managed?.name ??
        (metadataUrl === GATEWAY_METADATA_URL
          ? "Production MCP Gateway"
          : "External client"),
      grant_types: ["client_credentials", "urn:ietf:params:oauth:grant-type:token-exchange"],
      token_endpoint_auth_method: "private_key_jwt",
      jwks_uri: `${new URL(metadataUrl).origin}/oauth/jwks.json`,
    },
  };
}

/** A fixture endpoint for a catalog server - never a real host. */
export const mcpEndpointFor = (serviceId: string): string =>
  `https://${serviceId.replace(/^mcp-/, "")}.mcp.contoso.com/mcp`;
